/**
 * Recurrence scope arithmetic — the CLIENT half of Major 2.
 *
 * THE DATABASE GETS NO RRULE IMPLEMENTATION. `delete_event_series` and
 * `update_event_series` are plain plpgsql: they move rows, append an exDate and
 * clamp an UNTIL, but every INSTANT they need — `nextStart`, `nextEnd`,
 * `newStart`, `newEnd`, `tailId`, `tailCount` — is computed here and passed in
 * as `p_instants`. A third copy of the rule engine is exactly the trap this
 * repo has already been bitten by having two of, and the two engines that DO
 * exist (the expander cron and Major 1's client projection) are the same module
 * this one calls.
 *
 * A PURE LEAF, on purpose: no supabase import, no store, no clock beyond what a
 * caller hands in. That is what lets vitest reach all of it — the plpgsql
 * bodies it feeds are unreachable from any test in this repo.
 *
 * @module features/family-calendar/services/recurrenceScope
 */

import {
  expandOccurrences,
  toUtcDayStart,
} from '../../../../supabase/functions/_shared/rrule';
import type {
  CalendarEvent,
  EventUpdate,
  RecurrenceRule,
  RecurringEventUpdateScope,
  SeriesInstants,
  SeriesTarget,
} from '../types/event.types';

// ============================================
// Day / instant primitives
// ============================================

/**
 * The UTC day an instant falls on, as epoch milliseconds — the exact key both
 * rule engines compare exDates by.
 *
 * NOT `toLocalDateKey`. A 23:30-local event in UTC+3 sits on the NEXT UTC day,
 * so the local key and this one disagree for a real slice of every evening
 * series; using the local one would write an exDate that cancels the wrong day.
 */
export function utcDayKey(instant: Date): number {
  return toUtcDayStart(instant).getTime();
}

/**
 * The UNTIL value that truncates a series immediately BEFORE `instant`.
 *
 * ***  UNTIL IS INCLUSIVE. THE −1 ms IS NOT A ROUNDING HABIT.  ***
 * `expandOccurrences` rejects an instant only when
 * `instant.getTime() > upperBound.getTime()`, so writing `until = T` KEEPS the
 * very occurrence being cut — and it surfaces as an off-by-one in the calendar
 * rather than as a rule bug, which is why it would survive review. Anything
 * that writes an UNTIL must go through this function.
 */
export function untilBefore(instant: Date): string {
  return new Date(instant.getTime() - 1).toISOString();
}

/**
 * Append one instant to a rule's exDates, without duplicating it.
 *
 * THE CLIENT HALF OF THE APPEND `delete_event_series` / `update_event_series`
 * DO SERVER-SIDE. It lives here rather than in the store so that the optimistic
 * rule and the rule the RPC returns are produced by one piece of code, and so
 * that `seriesWrites.durability.test.ts` can feed THE REAL transformation back
 * through the engine instead of a look-alike written next to the assertion.
 *
 * Compared as INSTANTS: the same moment is spelled `…+00:00` by PostgREST and
 * `…000Z` by the client, so a string `includes` would append a duplicate that
 * the SQL's `@>` dedup would then refuse to recognise.
 */
export function withExDate(
  rule: RecurrenceRule,
  instantIso: string
): RecurrenceRule {
  const existing = rule.exDates ?? [];
  const targetMs = Date.parse(instantIso);
  if (existing.some((iso) => Date.parse(iso) === targetMs)) return rule;
  return { ...rule, exDates: [...existing, instantIso] };
}

/**
 * Clamp a rule to end just before `instant` — COUNT dropped in favour of UNTIL.
 *
 * The client half of D-c's truncation, here for the same reason
 * {@link withExDate} is. Dropping COUNT is exact rather than approximate:
 * `until` is the engine's upper bound and `count` is its remaining-slots cap, so
 * leaving both would let COUNT keep generating past the cut.
 */
export function withUntilBefore(
  rule: RecurrenceRule,
  instant: Date
): RecurrenceRule {
  const { count: _dropped, ...rest } = rule;
  return { ...rest, until: untilBefore(instant) };
}

/**
 * Widening probes for {@link nextOccurrenceAfter}.
 *
 * `expandOccurrences` takes a horizon, not a "give me the next one" cursor, so
 * finding the first surviving occurrence means asking over a window. Probing
 * outward instead of asking for one huge window matters: the engine's
 * 1000-occurrence guard is spent on the window it is given, so a daily series
 * asked about 100 years would exhaust the guard long before the loop ended and
 * answer nothing at all. Each probe is tried in turn and the first one that
 * yields an instant wins; the last is wide enough for a yearly rule at any
 * interval the type can carry.
 */
const NEXT_OCCURRENCE_PROBE_DAYS: readonly number[] = [45, 400, 4000, 40000];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The first instant the rule still produces after `masterStart` — the
 * occurrence a „изтрий само това" on the master's OWN instant rolls the master
 * forward onto.
 *
 * exDates and UNTIL are honoured for free, because this delegates to the same
 * engine: a cancelled next occurrence is skipped and the roll lands on the one
 * after it, and a series whose rule is exhausted answers `null` (which the RPC
 * reads as "delete the master outright").
 */
export function nextOccurrenceAfter(
  masterStart: Date,
  rule: RecurrenceRule
): Date | null {
  if (!Number.isFinite(masterStart.getTime())) return null;
  for (const days of NEXT_OCCURRENCE_PROBE_DAYS) {
    const horizon = new Date(masterStart.getTime() + days * MS_PER_DAY);
    const found = expandOccurrences({ start: masterStart, rule, horizon })[0];
    if (found) return found;
  }
  return null;
}

/**
 * The COUNT the TAIL series of a `this_and_following` split inherits, or
 * `undefined` when the original rule was not COUNT-bounded.
 *
 * `count - (1 + |occurrences strictly before T|)`: the master itself is
 * occurrence #1 (which `expandOccurrences` deliberately excludes from its
 * output, hence the `1 +`), and everything the head series keeps is spent.
 * Clamped at 1, because a rule carrying `count: 0` would expand to nothing and
 * the tail would be a series with no occurrences at all.
 *
 * Unreachable through today's UI — nothing can author a COUNT — but the engine
 * honours COUNT and the type carries it, so the arithmetic is here rather than
 * discovered later by whoever adds the picker.
 */
export function splitCount(
  rule: RecurrenceRule,
  masterStart: Date,
  occurrenceStart: Date
): number | undefined {
  if (typeof rule.count !== 'number' || rule.count <= 0) return undefined;
  const spent = expandOccurrences({
    start: masterStart,
    rule,
    horizon: new Date(occurrenceStart.getTime() - 1),
  }).length;
  return Math.max(1, rule.count - (1 + spent));
}

/**
 * Does the master's rule ACTUALLY GENERATE `instant`?
 *
 * ***  THIS IS THE CLIENT HALF OF 029's `p_instants.exDate` CONTRACT.  ***
 *
 * The one rule the migration states and restates: AN exDate MAY ONLY EVER NAME
 * AN INSTANT THE RULE GENERATES. `expandOccurrences` builds `exDays` through
 * `toUtcDayStart`, so ONE exDate cancels a WHOLE UTC DAY of the series — which
 * is lossless only while the instant named is one the engine would have
 * produced anyway. Write an exDate for a MOVED occurrence (Tuesday 09:00 dragged
 * to Wednesday 16:00) and you cancel all of Wednesday, taking that day's own
 * genuine 09:00 occurrence with it, permanently, with no UI that can undo it.
 *
 * The database cannot answer this — it has no rule engine, by decision — so it
 * decides structurally instead (no row / `is_occurrence = true` ⇒ provably a
 * rule instant; an AUTHORED exception ⇒ it cannot tell, and SKIPS unless the
 * client answers). This is the answer, and it is strictly better than the
 * structural one in every case: it is right about an authored exception BOTH
 * ways round, and it also refuses an exDate that a since-written `until` or a
 * since-written exDate for the same day has already made redundant.
 *
 * THE ENGINE'S 1000-OCCURRENCE GUARD IS NOT A HOLE HERE, and the argument is
 * worth keeping: the horizon is `instant` itself, and the loop up to it is
 * identical to the loop any WIDER expansion runs first. So if the projection, or
 * the expander, produced this instant at all — and something must have, or there
 * would be nothing to act on — then this call reproduces it. And if the guard
 * does cut the answer short, the engine that would REGENERATE the instant is cut
 * short at exactly the same place, so no exDate is owed.
 */
export function isRuleInstant(
  masterStart: Date,
  rule: RecurrenceRule,
  instant: Date
): boolean {
  const instantMs = instant.getTime();
  if (!Number.isFinite(instantMs) || !Number.isFinite(masterStart.getTime())) {
    return false;
  }
  // The master's own start is occurrence #1, which `expandOccurrences`
  // deliberately excludes; it is also the ANCHOR, which never takes an exDate
  // (after the roll it sits at or below the new start, where `accept()` never
  // even looks). Both callers treat it as "no exDate owed", so answer false.
  if (instantMs <= masterStart.getTime()) return false;
  return expandOccurrences({ start: masterStart, rule, horizon: instant }).some(
    (d) => d.getTime() === instantMs
  );
}

// ============================================
// The rule an edit is allowed to SEND
// ============================================

/**
 * The `recurrence` value a scoped EDIT may put on the wire.
 *
 * ****************************************************************************
 * ***  AN EDIT THAT DOES NOT INTEND TO CHANGE THE EXCEPTION SET MUST NOT   ***
 * ***  SEND ONE. THIS IS RISK 24, AND IT IS THE REASON THE RPC EXISTS.     ***
 * ****************************************************************************
 * `EditEventModal` offers exactly one rule control — the frequency picker — and
 * merges its answer over the master's stored rule so nothing else is lost
 * (decision 17). That merge reads the master out of `state.events`, i.e. out of
 * a snapshot that is stale by however long the realtime round trip takes.
 *
 * Sent whole to scope `all`, that stale copy is a WHOLE-JSONB WRITE, and 029's
 * E-d step 1 preservation cannot help: it folds `exDates`/`until` back from the
 * stored row only when the patch's rule OMITS the key. Present-but-stale is
 * indistinguishable from deliberate, so the write wins — and another member's
 * „отмени вторник", written thirty seconds earlier, is silently gone. It gets
 * worse on the way out: the merged rule now DIFFERS from the stored one, so
 * `touchesSchedule` reads it as a rule change and rebuilds every generated
 * child of the series — for what the user did as a title edit.
 *
 * So for scope `all` the two keys the server preserves are DROPPED, which is
 * the only way to say „I am not touching the exception set" in a jsonb the
 * server merges by key presence. `count` stays: nothing preserves it
 * server-side, and nothing but the anchor roll can change it.
 *
 * ***  ONLY SCOPE `all`, AND THE OTHER TWO LEGS ARE NOT AN OVERSIGHT.  ***
 *  - `this` never carries a rule at all — `calendarStore.updateEventSeries`
 *    strips the key before the patch is built, because an exception row has no
 *    rule column to write.
 *  - `this_and_following` does NOT patch the master's rule from `p_patch`
 *    (E-c step 2 derives it from the stored row: `recurrence - 'count' ||
 *    until`), so there is nothing here to clobber. Its `p_patch` rule is the
 *    CONSTRUCTOR for the tail master, and 029 filters `exDates >= T` out of it
 *    to seed the new series — dropping them there would resurrect every
 *    occurrence the family had cancelled above the split.
 *
 * ***  ...EXCEPT WHEN THE SERVER DELEGATES `this_and_following` TO `all`.  ***
 * That carve-out for the split leg is right only while the leg STAYS a split.
 * 029 turns `this_and_following` at any `T <= M.start_date` into scope `all`
 * (E-c's guard, and the same `<=` in `delete_event_series`), and the delegated
 * leg then runs E-d step 1 — the whole-jsonb merge this function exists to keep
 * a stale rule out of. The scope the CALLER holds is the one the user picked,
 * not the one the server will run, so this function cannot see the difference;
 * {@link delegatesToAll} can, and `calendarStore.updateEventSeries` applies
 * {@link withoutServerPreservedKeys} on that answer before the patch is built.
 * Nothing here changes: the split's own rule stays whole.
 */
export function ruleForScopedEdit(
  existing: RecurrenceRule | undefined,
  frequency: RecurrenceRule['frequency'],
  scope: RecurringEventUpdateScope | null
): RecurrenceRule {
  const merged: RecurrenceRule = {
    ...existing,
    frequency,
    interval: existing?.interval ?? 1,
  };
  if (scope !== 'all') return merged;
  return withoutServerPreservedKeys(merged);
}

/**
 * Drop the two keys 029 folds back from the stored row — the only way to say
 * „I am not touching the exception set" in a jsonb the server merges by KEY
 * PRESENCE.
 *
 * `count` deliberately stays: nothing preserves it server-side, and nothing but
 * the anchor roll can change it.
 */
export function withoutServerPreservedKeys(
  rule: RecurrenceRule
): RecurrenceRule {
  const { exDates: _serverPreserved, until: _alsoPreserved, ...rest } = rule;
  return rest;
}

/**
 * Will the SERVER run this leg as scope `all` rather than as the scope the user
 * picked?
 *
 * ***  THE CLIENT CAN ANSWER THIS, AND HAS TO.  ***
 * Both RPCs re-scope `this_and_following` to `all` whenever the targeted
 * instant sits at or below the master's own start: no occurrence of a series
 * exists below its master's start, so {occurrences at or after T} is the whole
 * series and the leg IS `all`. The predicate is `p_occurrence_start <=
 * v_master.start_date` — `<=`, not `=`, because E-b's anchor roll, an E-d `all`
 * move and an E-a move to an earlier instant all leave real, clickable
 * occurrences BELOW the master's start.
 *
 * `isMasterOccurrence` is NOT this question and must not be used as it: it is
 * the `=` case only, and the strictly-below cases are exactly the ones a reader
 * forgets exist.
 *
 * Two client-side consequences hang off this answer, and they are the two
 * regressions the `<=` delegation introduced:
 *   * the patch's rule must be stripped as if the user had picked `all`
 *     (risk 24 — see {@link ruleForScopedEdit});
 *   * no tail master is created, so the RPC answers `tail: null` and the
 *     optimistically-minted tail row must be reconciled away.
 *
 * Compared as INSTANTS, for the usual reason: PostgREST answers
 * `2026-09-01T06:00:00+00:00` where the client mints `2026-09-01T06:00:00.000Z`.
 * An unparseable instant answers `false` — the RPC would reject the call
 * outright, and guessing `all` there would strip a rule for a write that never
 * happens.
 */
export function delegatesToAll(
  target: SeriesTarget,
  master: CalendarEvent,
  scope: RecurringEventUpdateScope
): boolean {
  if (scope !== 'this_and_following') return false;
  const targetMs = Date.parse(target.occurrenceStart);
  const masterStartMs = Date.parse(master.startDate);
  if (!Number.isFinite(targetMs) || !Number.isFinite(masterStartMs)) {
    return false;
  }
  return targetMs <= masterStartMs;
}

// ============================================
// Target resolution
// ============================================

/**
 * Resolve the row a modal is showing, plus the instant the user clicked, into
 * the `(masterId, occurrenceStart)` pair every scope leg is addressed by.
 *
 * Returns `null` for a NON-SERIES event, and that null is the switch the modals
 * hang off: null means "today's plain confirm, today's plain
 * `updateEvent`/`deleteEvent`", non-null means "offer the scope chooser".
 *
 * `event` is always a REAL row — the click path resolves every series pill to
 * `parentEventId ?? id`, so a projection's synthetic id never reaches here and
 * `event` is the master (or a plain event, or a child reached by deep link).
 *
 * `occurrenceId` is `null` when the targeted instant has NO ROW YET. That is
 * the normal case for a projection, and it is why the RPCs take an instant
 * rather than a row id: there is nothing to name until the cron materialises
 * it, and the leg must work before then.
 */
export function resolveSeriesTarget(
  event: CalendarEvent,
  events: readonly CalendarEvent[],
  occurrenceStart?: string
): SeriesTarget | null {
  const isSeriesRow = event.parentEventId != null || event.recurrence != null;
  if (!isSeriesRow) return null;

  const masterId = event.parentEventId ?? event.id;
  // Falling back to the row's own start covers the deep-link path, where a
  // child row is selected by its own id and no instant is threaded through.
  const instantIso = occurrenceStart ?? event.startDate;
  const instantMs = Date.parse(instantIso);
  if (!Number.isFinite(instantMs)) return null;

  const master = events.find((e) => e.id === masterId);
  const isMasterOccurrence =
    master != null && Date.parse(master.startDate) === instantMs;

  // Compared as INSTANTS, never as strings: PostgREST answers
  // `2026-09-01T06:00:00+00:00` and the client mints
  // `2026-09-01T06:00:00.000Z` for the same moment.
  const row = events.find(
    (e) =>
      (e.id === masterId || e.parentEventId === masterId) &&
      Date.parse(e.startDate) === instantMs
  );

  return {
    masterId,
    occurrenceStart: instantIso,
    occurrenceId: row?.id ?? null,
    isMasterOccurrence,
  };
}

/**
 * The row that OWNS THE RECURRENCE RULE for whatever row a modal is showing.
 *
 * ***  THE RULE IS A MASTER-ONLY COLUMN. READ IT OFF THE MASTER, NOT OFF     ***
 * ***  WHATEVER ROW HAPPENED TO BE CLICKED.                                  ***
 * Every child of a series — generated or authored — carries `recurrence: null`
 * (`006`; the expander writes children with no rule). `EditEventModal` fills
 * its recurrence selector from that column and MERGES its edit back over it, so
 * a child row reaching the modal would fill the selector with „Без повторение",
 * and one ordinary save at scope `all` or `this_and_following` would then send
 * `recurrence: null` — clearing the master's rule and taking every generated
 * child with it. One wrong row, a whole series gone.
 *
 * That the modal only ever RECEIVES the master is true today and is true only
 * BY CONVENTION of three call sites: the click path resolves a series pill to
 * `parentEventId ?? id`, and a deep link resolves to a real row that may well
 * be a child (`resolveSeriesTarget` says so in as many words). This function is
 * what makes the invariant structural instead — the rule is looked up on the
 * master's own row, whichever row was handed in.
 *
 * A child whose master is NOT in `events` falls back to the child itself, and
 * that is safe rather than a hole: `calendarStore.updateEventSeries` and
 * `deleteEventSeries` both look the master up in the same array and refuse the
 * write LOUDLY when it is missing, so no patch built from the fallback can
 * reach the RPC.
 */
export function resolveSeriesMaster(
  event: CalendarEvent,
  events: readonly CalendarEvent[]
): CalendarEvent {
  const masterId = event.parentEventId;
  if (masterId == null) return event;
  return events.find((e) => e.id === masterId) ?? event;
}

/**
 * The AUTHORED EXCEPTION row standing at the clicked instant, or `null`.
 *
 * ***  WHAT THE SHEET READS AND WHERE THE SAVE GOES ARE TWO QUESTIONS.  ***
 * The click path deliberately routes every series pill to
 * `(masterId, occurrenceStart)` — a projection has no row to name, so the WRITE
 * has to be addressed by instant. That is right, and it stays. But it made both
 * modals render the MASTER's content for every click, which is wrong the moment
 * a per-occurrence edit exists: retitle Tuesday's pill „Тренировка — отменена
 * зала", reopen it, and the sheet shows the series title as if the edit had
 * never happened — and saving anything from that form writes the master's title
 * straight back over the exception.
 *
 * Narrowed to `is_occurrence === false` on purpose. A GENERATED child is a copy
 * of the master by construction (the expander's refresh pass keeps it that way),
 * so reading it would change nothing when it is current and would show STALE
 * content when the refresh has not run yet. A projection has no row at all.
 * Only an authored exception can differ from its master on purpose.
 *
 * The master itself is excluded: `resolveSeriesTarget` resolves the anchor
 * instant to the master row, and a master is not an exception to itself.
 */
export function resolveOccurrenceRow(
  target: SeriesTarget | null,
  events: readonly CalendarEvent[]
): CalendarEvent | null {
  if (target === null || target.occurrenceId === null) return null;
  if (target.isMasterOccurrence) return null;
  const row = events.find((e) => e.id === target.occurrenceId);
  if (row === undefined) return null;
  if (row.parentEventId !== target.masterId) return null;
  return row.isOccurrence === false ? row : null;
}

// ============================================
// p_instants
// ============================================

/** Master duration in ms, or `null` when the master has no end. */
function masterDurationMs(master: CalendarEvent): number | null {
  if (master.endDate == null) return null;
  const startMs = Date.parse(master.startDate);
  const endMs = Date.parse(master.endDate);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return endMs - startMs;
}

/**
 * Was this leg handed a genuine MOVE — a destination instant different from the
 * one the user clicked?
 *
 * `updates === undefined` is the DELETE path, which never moves anything.
 */
function movesOccurrence(target: SeriesTarget, updates?: EventUpdate): boolean {
  if (updates?.startDate == null) return false;
  const parsed = Date.parse(updates.startDate);
  const targetMs = Date.parse(target.occurrenceStart);
  return Number.isFinite(parsed) && Number.isFinite(targetMs)
    ? parsed !== targetMs
    : false;
}

/**
 * The exDate this leg must write — `p_instants.exDate`, and the SAME value the
 * store appends optimistically.
 *
 * Three answers, and they are three different things:
 *   * `undefined` — this leg writes NO exDate at all, and the key is omitted so
 *     the SQL keeps its own (identical) reasoning. Scopes `all` and
 *     `this_and_following` clamp UNTIL instead; the anchor roll needs none; a
 *     content-only edit of one occurrence needs none, because the exception row
 *     occupies `(parent_event_id, start_date)` and the expander's upsert is
 *     `on conflict do nothing`.
 *   * `null` — this IS an exDate-writing leg, and no exDate is owed: the
 *     instant is not one the rule generates (an already-moved exception), so
 *     naming it would cancel the whole UTC day it happens to sit on.
 *   * an ISO instant — write it.
 *
 * WHY THE KEY IS SENT RATHER THAN LEFT TO THE SQL. 029 skips the exDate for
 * every AUTHORED exception unless the client answers, and that direction is
 * deliberately the safe one — but it is only HALF the fix, and the half that
 * was missing made two things go wrong on screen: deleting an occurrence the
 * family had edited in place did not stick on the first press (the server wrote
 * no exDate, so the projection came straight back), and MOVING such an
 * occurrence left its original instant live for the cron to regenerate a
 * duplicate at. {@link isRuleInstant} is the answer the SQL asked for.
 *
 * The value is normalised through `toISOString()` so it matches the SQL's
 * `to_char(… 'YYYY-MM-DD"T"HH24:MI:SS.MS"Z"')` CHARACTER FOR CHARACTER — the
 * server's `@>` dedup and the client's own append have to agree that a
 * previously-written entry is the same entry.
 */
export function seriesExDate(
  target: SeriesTarget,
  master: CalendarEvent,
  scope: RecurringEventUpdateScope,
  updates?: EventUpdate
): string | null | undefined {
  if (scope !== 'this') return undefined;
  // The anchor rolls the master forward instead; see isRuleInstant.
  if (target.isMasterOccurrence) return undefined;
  // A DELETE always vacates the instant; an EDIT only when it MOVES.
  if (updates !== undefined && !movesOccurrence(target, updates)) {
    return undefined;
  }

  const targetMs = Date.parse(target.occurrenceStart);
  const masterStartMs = Date.parse(master.startDate);
  if (
    master.recurrence == null ||
    !Number.isFinite(targetMs) ||
    !Number.isFinite(masterStartMs)
  ) {
    return null;
  }

  const instant = new Date(targetMs);
  return isRuleInstant(new Date(masterStartMs), master.recurrence, instant)
    ? instant.toISOString()
    : null;
}

/**
 * Build the `p_instants` payload for one leg.
 *
 * `updates` is the ALREADY-REBASED patch (see {@link rebaseUpdatesForScope});
 * its `startDate` is the destination of the edit expressed in the same frame as
 * `target.occurrenceStart`, so `newStart` is emitted only when the two actually
 * differ. Emitting it unconditionally would be harmless for the SQL — `v_t2`
 * coalesces to `p_occurrence_start` — but it would make the "was this
 * occurrence MOVED?" question, which decides whether an exDate is written,
 * answerable only by comparing timestamps in plpgsql.
 *
 * Keys are spread conditionally (`exactOptionalPropertyTypes` is on, and the
 * SQL side reads `p_instants ? 'newEnd'` — a key present with a JSON null is
 * NOT the same as an absent one there).
 */
export function seriesInstants(
  target: SeriesTarget,
  master: CalendarEvent,
  scope: RecurringEventUpdateScope,
  updates?: EventUpdate
): SeriesInstants {
  const targetMs = Date.parse(target.occurrenceStart);
  const durationMs = masterDurationMs(master);

  // ---- the anchor roll ----------------------------------------------------
  // Only scope `this` on the master's own instant rolls the anchor. `all`
  // deletes/edits the master outright and `this_and_following` from occurrence
  // #1 is delegated to `all` server-side, so neither needs a next instant.
  let nextStart: Date | null = null;
  if (scope === 'this' && target.isMasterOccurrence && master.recurrence) {
    nextStart = nextOccurrenceAfter(
      new Date(Date.parse(master.startDate)),
      master.recurrence
    );
  }

  // ---- the destination of an EDIT ----------------------------------------
  let newStartMs: number | null = null;
  let newEndMs: number | null = null;
  if (updates?.startDate != null) {
    const parsed = Date.parse(updates.startDate);
    if (Number.isFinite(parsed) && parsed !== targetMs) newStartMs = parsed;
  }
  if (updates?.endDate != null) {
    const parsed = Date.parse(updates.endDate);
    if (Number.isFinite(parsed)) newEndMs = parsed;
  }

  // ---- the split ----------------------------------------------------------
  let tailId: string | undefined;
  let tailCount: number | undefined;
  if (scope === 'this_and_following' && updates !== undefined) {
    // Minted here so the optimistic store row and the persisted row share ONE
    // id: the RPC inserts with `on conflict (id) do nothing`, so a retry of the
    // same call is idempotent instead of producing a second tail series.
    tailId = crypto.randomUUID();
    if (master.recurrence && Number.isFinite(targetMs)) {
      tailCount = splitCount(
        master.recurrence,
        new Date(Date.parse(master.startDate)),
        new Date(targetMs)
      );
    }
  }

  // ---- the exDate ---------------------------------------------------------
  // `undefined` means "this leg writes none"; `null` is a real answer ("none is
  // owed") and travels as a JSON null, which the SQL reads through
  // `p_instants ? 'exDate'` — key-present-with-null is NOT the same as absent
  // there, and that is the whole point of sending it.
  const exDate = seriesExDate(target, master, scope, updates);

  return {
    ...(exDate !== undefined && { exDate }),
    ...(nextStart != null && { nextStart: nextStart.toISOString() }),
    ...(nextStart != null &&
      durationMs != null && {
        nextEnd: new Date(nextStart.getTime() + durationMs).toISOString(),
      }),
    ...(newStartMs != null && { newStart: new Date(newStartMs).toISOString() }),
    ...(newEndMs != null && { newEnd: new Date(newEndMs).toISOString() }),
    ...(tailId !== undefined && { tailId }),
    ...(tailCount !== undefined && { tailCount }),
  };
}

// ============================================
// Rebasing a patch built against one occurrence
// ============================================

/**
 * Re-express a patch the user authored AGAINST ONE OCCURRENCE in the frame the
 * chosen scope actually writes to.
 *
 * THE BUG THIS EXISTS TO PREVENT, in full: a daily series starts
 * 2026-09-01 09:00; the user clicks the 2026-09-15 pill; the modal (correctly)
 * shows 15 September, because that is the day they clicked. They change the
 * title and pick „Всички събития". The form still says 2026-09-15, so an
 * un-rebased patch sends `start_date = 2026-09-15T09:00` for the MASTER — and
 * the whole series jumps forward a fortnight on a title edit. Every occurrence
 * before the 15th disappears and every reminder already scheduled is wrong.
 *
 * So what travels is the DELTA the user actually expressed:
 *   - `this` / `this_and_following` — the destination instant itself; the RPC
 *     writes the exception (or the tail master) exactly there.
 *   - `all` — the same delta applied to the MASTER's own start, so a series
 *     edited from its fifth occurrence moves by the amount the user moved it
 *     and not to the day they happened to be looking at.
 *
 * Duration is preserved as edited: the end is re-derived from the new start
 * plus whatever span the form now describes, so shortening a meeting from the
 * fifth occurrence shortens all of them without also relocating them.
 */
export function rebaseUpdatesForScope(
  updates: EventUpdate,
  target: SeriesTarget,
  master: CalendarEvent,
  scope: RecurringEventUpdateScope
): EventUpdate {
  if (updates.startDate == null) return updates;

  const targetMs = Date.parse(target.occurrenceStart);
  const formStartMs = Date.parse(updates.startDate);
  if (!Number.isFinite(targetMs) || !Number.isFinite(formStartMs)) {
    return updates;
  }

  const formEndMs =
    updates.endDate != null ? Date.parse(updates.endDate) : Number.NaN;
  const spanMs = Number.isFinite(formEndMs) ? formEndMs - formStartMs : null;

  if (scope !== 'all') {
    // The destination IS the form's instant. Nothing to rebase — but the end is
    // still normalised through the same span arithmetic so both branches agree.
    return withStartAndEnd(updates, formStartMs, spanMs);
  }

  const deltaMs = formStartMs - targetMs;
  const masterStartMs = Date.parse(master.startDate);
  if (!Number.isFinite(masterStartMs)) return updates;
  return withStartAndEnd(updates, masterStartMs + deltaMs, spanMs);
}

/**
 * Replace `startDate`/`endDate` on a patch.
 *
 * The three states of `endDate` are preserved exactly, because
 * `exactOptionalPropertyTypes` is on and they mean different things upstream:
 * KEY ABSENT is "not edited", an ISO string is the new end, and an explicit
 * `undefined` is „изчисти" — the state the store's diff loop turns into a
 * "removed" change record. (`eventUpdatesToDTO` only carries that third state
 * as far as the server for `recurrence` today; `endDate` clearing is the same
 * pre-existing gap every other optional column has, and this function does not
 * widen it in either direction.)
 */
function withStartAndEnd(
  updates: EventUpdate,
  startMs: number,
  spanMs: number | null
): EventUpdate {
  const { endDate: _replaced, ...rest } = updates;
  const hadEndKey = 'endDate' in updates;
  const nextEnd =
    spanMs != null && spanMs !== 0
      ? new Date(startMs + spanMs).toISOString()
      : undefined;
  return {
    ...rest,
    startDate: new Date(startMs).toISOString(),
    ...(hadEndKey && { endDate: nextEnd }),
  };
}
